import * as React from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  IconButton,
  TablePagination,
  Button,
  Autocomplete,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL;

interface City {
  citiesId: number;
  name: string;
}
interface Manager {
  managerId: number;
  name: string;
}
interface Shop {
  shopId: number;
  name: string;
  address: string;
  workTime?: string;
  city?: City | null;
  manager?: Manager | null;
}

export default function Shops() {
  const navigate = useNavigate();

  const [shops, setShops] = React.useState<Shop[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [limit, setLimit] = React.useState(10);
  const [search, setSearch] = React.useState('');
  const [sortBy, setSortBy] = React.useState('name');
  const [sortOrder, setSortOrder] = React.useState<'ASC' | 'DESC'>('ASC');
  const [cityId, setCityId] = React.useState('');
  const [managerId, setManagerId] = React.useState('');
  const [cities, setCities] = React.useState<City[]>([]);
  const [managers, setManagers] = React.useState<Manager[]>([]);

  const fetchShops = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/shops`, {
        params: {
          page,
          limit,
          sortBy,
          sortOrder,
          q: search,
          cityId: cityId || undefined,
          managerId: managerId || undefined,
        },
      });
      setShops(data.data || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    axios
      .get(`${API_URL}/city`, { params: { limit: 10000 } })
      .then(({ data }) => setCities(data.data || []))
      .catch((err) => console.error(err));

    axios
      .get(`${API_URL}/managers`, { params: { limit: 10000 } })
      .then(({ data }) => setManagers(data.data || []))
      .catch((err) => console.error(err));
  }, []);

  React.useEffect(() => {
    fetchShops();
  }, [page, limit, search, sortBy, sortOrder, cityId, managerId]);

  const handleDelete = async (shopId: number) => {
    if (!confirm('Видалити цей магазин?')) return;
    try {
      await axios.delete(`${API_URL}/shops/${shopId}`);
      fetchShops();
    } catch (err) {
      console.error(err);
    }
  };

  const [sortValue, setSortValue] = React.useState('name_asc');

  const handleSortChange = (value: string) => {
    setSortValue(value);

    switch (value) {
      case 'name_asc':
        setSortBy('name');
        setSortOrder('ASC');
        break;
      case 'name_desc':
        setSortBy('name');
        setSortOrder('DESC');
        break;
      case 'address_asc':
        setSortBy('address');
        setSortOrder('ASC');
        break;
      case 'address_desc':
        setSortBy('address');
        setSortOrder('DESC');
        break;
      case 'created_desc':
        setSortBy('created_at');
        setSortOrder('DESC');
        break;
      case 'created_asc':
        setSortBy('created_at');
        setSortOrder('ASC');
        break;
      case 'updated_desc':
        setSortBy('updated_at');
        setSortOrder('DESC');
        break;
    }

    setPage(1);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Магазини
      </Typography>

      <Button
        variant="contained"
        sx={{ mb: 2 }}
        onClick={() => navigate('/shops/new')}
      >
        Створити магазин
      </Button>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Пошук магазину..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />

        <Select
          value={sortValue}
          onChange={(e) => handleSortChange(e.target.value)}
        >
          <MenuItem value="name_asc">Назва (А - Я)</MenuItem>
          <MenuItem value="name_desc">Назва (Я - А)</MenuItem>
          <MenuItem value="address_asc">Адреса (А - Я)</MenuItem>
          <MenuItem value="address_desc">Адреса (Я - А)</MenuItem>
          <MenuItem value="created_desc">Нові</MenuItem>
          <MenuItem value="created_asc">Старі</MenuItem>
          <MenuItem value="updated_desc">Оновлені</MenuItem>
        </Select>

        <Autocomplete
          sx={{ minWidth: 220 }}
          options={cities}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) =>
            option.citiesId === value.citiesId
          }
          value={cities.find((c) => String(c.citiesId) === cityId) || null}
          onChange={(_, value) => {
            setCityId(value ? String(value.citiesId) : '');
            setPage(1);
          }}
          renderInput={(params) => <TextField {...params} label="Місто" />}
        />

        <Autocomplete
          sx={{ minWidth: 240 }}
          options={managers}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) =>
            option.managerId === value.managerId
          }
          value={
            managers.find((m) => String(m.managerId) === managerId) || null
          }
          onChange={(_, value) => {
            setManagerId(value ? String(value.managerId) : '');
            setPage(1);
          }}
          renderInput={(params) => <TextField {...params} label="Менеджер" />}
        />
      </Box>

      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        Кількість магазинів: {total}
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Назва</TableCell>
              <TableCell>Адреса</TableCell>
              <TableCell>Місто</TableCell>
              <TableCell>Менеджер</TableCell>
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Завантаження...
                </TableCell>
              </TableRow>
            ) : shops.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Немає магазинів
                </TableCell>
              </TableRow>
            ) : (
              shops.map((shop) => (
                <TableRow key={shop.shopId}>
                  <TableCell>{shop.name}</TableCell>
                  <TableCell>{shop.address}</TableCell>
                  <TableCell>{shop.city?.name || '—'}</TableCell>
                  <TableCell>{shop.manager?.name || '—'}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={() => navigate(`/shops/edit/${shop.shopId}`)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(shop.shopId)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page - 1}
        onPageChange={(_, newPage) => setPage(newPage + 1)}
        rowsPerPage={limit}
        onRowsPerPageChange={(e) => {
          setLimit(parseInt(e.target.value, 10));
          setPage(1);
        }}
        rowsPerPageOptions={[10, 20, 50, 100]}
        labelRowsPerPage="Кількість записів"
      />
    </Box>
  );
}
